'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import * as React from 'react'
import ReactDOM from 'react-dom'
import { useFormikContext } from 'formik'
import Icon from '@mdi/react'
import { mdiFilterCheck, mdiFilterOff } from '@mdi/js'

// walrus
import {
  useSelect,
  SelectButton,
  SelectMenu,
  SelectOption
} from '@components/kit/select'
import Box from '@components/kit/box'
import Button from '@components/kit/button'
import Text from '@components/kit/text'
import { ThemeVariantProvider } from '@components/kit/_support'
import { StatusType } from '@store/Opportunity/types'
import { titleCase } from '@utils/string-formatters'
import { colors } from '@theme'

// lib
import { MemberListFormValues } from './member-list'

/* -----------------------------------------------------------------------------
 * StatusFilterSelect
 * -------------------------------------------------------------------------- */

export type Item = {
  label: string
  status: StatusType
  themeVariant: 'default' | 'violet' | 'gray'
}

export type Section = {
  title: string
  items: Item[]
}

const selectOptions: Section[] = [
  {
    title: 'Chat',
    items: [
      {
        label: 'Recent Messages',
        // @ts-ignore : status 0 is a clear filter option
        status: 0,
        themeVariant: 'gray'
      }
    ]
  },
  {
    title: 'New Opportunities',
    items: [
      {
        label: 'PharmD - Approval Needed',
        status: StatusType.PHARMD_APPROVAL_NEEDED,
        themeVariant: 'violet'
      }
    ]
  },
  {
    title: 'Opportunities in Progress',
    items: [
      {
        label: 'Member - Outreach Needed',
        status: StatusType.MEMBER_OUTREACH_NEEDED,
        themeVariant: 'default'
      },
      {
        label: 'Member - Pending',
        status: StatusType.MEMBER_PENDING,
        themeVariant: 'default'
      },
      {
        label: 'Doctor - Outreach Needed',
        status: StatusType.DOCTOR_OUTREACH_NEEDED,
        themeVariant: 'default'
      },
      {
        label: 'Doctor - Pending',
        status: StatusType.DOCTOR_PENDING,
        themeVariant: 'default'
      },
      {
        label: 'Pharmacy - Pending',
        status: StatusType.PHARMACY_PENDING,
        themeVariant: 'default'
      }
    ]
  },
  {
    title: 'Opportunities Denied',
    items: [
      {
        label: 'PharmD - Denied',
        status: StatusType.PHARMD_DENIED,
        themeVariant: 'gray'
      },
      {
        label: 'Member - Denied',
        status: StatusType.MEMBER_DENIED,
        themeVariant: 'gray'
      },
      {
        label: 'Doctor - Denied',
        status: StatusType.DOCTOR_DENIED,
        themeVariant: 'gray'
      },
      {
        label: 'Pharmacy - Denied',
        status: StatusType.PHARMACY_DENIED,
        themeVariant: 'gray'
      }
    ]
  },
  {
    title: 'Opportunities Complete',
    items: [
      {
        label: 'Pharmacy - Approved',
        status: StatusType.PHARMACY_APPROVED,
        themeVariant: 'gray'
      }
    ]
  }
]

export const StatusFilterSelect = () => {
  const buttonRef = React.useRef(null)
  const menuRef = React.useRef(null)
  const { values, handleSubmit, setFieldValue } =
    useFormikContext<MemberListFormValues>()
  const items = selectOptions.reduce(
    (prev, curr) => [...prev, ...curr.items],
    [] as Item[]
  )
  const defaultItem = items[0]
  const activeItem =
    items.find(item => item.status === values.opportunity_status) ?? defaultItem
  const showClearFilter = (values.opportunity_status ?? 0) !== 0

  const {
    selectedItem,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    closeMenu
  } = useSelect<Item>({
    buttonRef,
    menuRef,
    items,
    itemToString: item => (item ? item.label : ''),
    selectedItem: activeItem,
    onSelectedItemChange: ({ selectedItem: nextItem }) => {
      if (!nextItem) {
        return
      }
      setFieldValue('opportunity_status', nextItem.status)
      handleSubmit()
    }
  })

  const handleClearFilter = () => {
    setFieldValue('opportunity_status', 0)
    handleSubmit()
    closeMenu()
  }

  return (
    <Box width='100%'>
      <Box ref={buttonRef}>
        <SelectButton
          {...getToggleButtonProps()}
          p='16'
          height={68}
          width={8}
          bg='white'
          fill='purple'
        >
          <Text truncate font='display.20.bold' textColor='purple'>
            {titleCase((selectedItem ?? activeItem).label)}
          </Text>
        </SelectButton>
      </Box>
      {ReactDOM.createPortal(
        <SelectMenu
          {...getMenuProps({ ref: menuRef })}
          minWidth='304px'
          height='100%'
          top='00'
          overflowY='auto'
        >
          {selectOptions.map(section => (
            <React.Fragment key={section.title}>
              <Box mt='24' mb='00'>
                <SelectOption.Title>{section.title}</SelectOption.Title>
              </Box>
              {section.items.map(item => (
                <ThemeVariantProvider
                  key={item.label}
                  value={item.themeVariant}
                >
                  <Box
                    flexDirection='row'
                    alignItems='center'
                    justifyContent='space-between'
                    width='100%'
                  >
                    <SelectOption.Badge
                      {...getItemProps({
                        item,
                        index: items.findIndex(
                          ({ label }: Item) => label === item.label
                        )
                      })}
                    >
                      <Box
                        flexDirection='row'
                        alignItems='center'
                        justifyContent='space-between'
                        width='100%'
                      >
                        <Text>{item.label.toUpperCase()}</Text>
                      </Box>
                    </SelectOption.Badge>
                    {item.status === activeItem.status && (
                      <Icon
                        path={mdiFilterCheck}
                        size='16px'
                        color={colors.darkGreen}
                      />
                    )}
                  </Box>
                </ThemeVariantProvider>
              ))}
            </React.Fragment>
          ))}
          {showClearFilter && (
            <Box
              mt='16'
              pt='16'
              px='12'
              pb='24'
              borderTopWidth={1}
              borderColor='shade03'
            >
              <Button variant='tertiary' size='fit' onPress={handleClearFilter}>
                <Box flexDirection='row' alignItems='center' gap='08'>
                  <Icon path={mdiFilterOff} size='18px' color={colors.purple} />
                  <Text font='text.14.medium' textColor='purple'>
                    Clear Filter
                  </Text>
                </Box>
              </Button>
            </Box>
          )}
        </SelectMenu>,
        document.getElementById('root')
      )}
    </Box>
  )
}

export default StatusFilterSelect
